// frontend/src/context/DataContext.tsx
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { fetchMeetingData } from '../api/meeting';
import api from '../api/client';
import { toast } from '../components/shared/toast';
import type {
  CurrentMeeting,
  FormScope,
  MeetingId,
  MeetingRow,
  RunnerId,
} from '../types/domain';

const scopeConfig: Record<FormScope, string[]> = {
  runner: [
    'runner_id','saddlecloth','hname','weight_declared','weight_carried','weight',
    'ohr','age','horse_id','fin','is_scratched',
    'trainer','jockey','margin','barrier','official_sp','is_apprentice',
    'jockey_claim','est_jockey_claim','sp_rank','prizemoney_won',
    'bsp','ppwap','morningwap','ppmax',
    'sex','sire','dam','damsire','foal_date','colour','yearborn','trainer_id',
    'jockey_id','rating','sp','expected_rating','sp_rating','rated_price','p_win','start_no','sip',
    'Jx','Bx','Dx','rating_bonus','200m','sal_rating','wfa_rating','wfa_allowance','persig','perlite','comment',
    'notes','horse_peak'
  ],
  race: [
    'race_id','race_no','race_name','feature_race_id','distance','track_type','group_race','prizemoney',
    'conditions_class','conditions_weight','conditions_weight_limit','conditions_age',
    'conditions_sex','jockey_claim_allowed','t_rating','ldr_time_results','l600_results','l600','l400','l200',
    't_surface','winner','bm_l','bm_e','c_l','c_e','par','class_rating',
    'class_true','starters','mse','mse_class','race_rating','r_ts', 'edist', 'esplit',
    'e_ts', 'race_class', 'mse_race', 'race_allowance', 'pm_1', 'Ox', 'flag', 'speed_mse',
    'plus_minus','rating_diff','par_time','historic_winner','historic_3rd','historic_median',
    'datacount','race_events','race_count',
  ],
  meeting: [
    'meeting_id','meetdate','state','track','track_id','is_trial','results_is_complete',
    'races_is_complete','fields_is_complete','interim_rating','final_rating','sectional_times','verified_ratings',
    'rail','bias','d_ts', 'track_class_offset'
  ]
};

const SCOPES = Object.keys(scopeConfig) as FormScope[];

/** Edits staged for aus_predictions, shaped `{ [runner_id]: { col: value } }`. */
type StagedEdits = Record<number, Record<string, unknown>>;

/** Pending FieldTable edits, shaped `{ [runner_id]: { field: value } }`. */
export type PendingRunnerFields = Record<string | number, Record<string, unknown>>;

export interface DataContextValue {
  ausRatings: MeetingRow[];
  currentMeeting: CurrentMeeting | null;
  loadMeeting: (meetingId: MeetingId, opts?: { fast?: boolean }) => Promise<MeetingRow[] | null | undefined>;
  updateField: (
    field: string,
    value: unknown,
    id?: number | null,
    explicitScope?: FormScope | null,
  ) => Promise<MeetingRow[] | null>;
  updateRunnerFields: (pending: PendingRunnerFields) => Promise<MeetingRow[] | null>;
  stagePersist: (runnerId: RunnerId | string, col: string, value: unknown) => void;
  persistForm: () => Promise<unknown>;
  isDirty: boolean;
  clearDirty: () => void;
  /** Fast read-only mode vs full editable+calc mode. */
  formActive: boolean;
  setFormActive: (active: boolean) => void;
}

const DataContext = createContext<DataContextValue | undefined>(undefined);

export const DataProvider = ({ children }: { children: ReactNode }) => {
  const [ausRatings, setAusRatings] = useState<MeetingRow[]>([]);
  const [currentMeeting, setCurrentMeeting] = useState<CurrentMeeting | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  // Fast read-only mode vs full editable+calc mode. Tabs read this to hide
  // computed columns (overlay/stake) and render values as text instead of
  // inputs when the form is inactive.
  const [formActive, setFormActive] = useState(false);
  const formActiveRef = useRef(formActive);
  formActiveRef.current = formActive;
  const loadSeqRef = useRef(0);

  // Refs for stable updateField closure — avoids recreating updateField on every data change
  const ausRatingsRef = useRef(ausRatings);
  const currentMeetingRef = useRef(currentMeeting);
  ausRatingsRef.current = ausRatings;
  currentMeetingRef.current = currentMeeting;

  // Edits destined for the DB (aus_predictions). Accumulated as the user edits
  // and flushed on explicit Save (persistForm).
  const persistRef = useRef<StagedEdits>({});

  /** Load meeting and hydrate ausRatings */
  const loadMeeting = useCallback(async (meetingId: MeetingId, { fast = false } = {}) => {
    const loadSeq = ++loadSeqRef.current;
    try {
      const meetingData = await fetchMeetingData(meetingId, { fast });
      if (loadSeq !== loadSeqRef.current) return null;

      const rows = meetingData.data || [];

      if (!rows.length) {
        setCurrentMeeting(null);
        setAusRatings([]);
        setIsDirty(false);
        return;
      }

      const firstRow = rows[0];
      setCurrentMeeting({
        meeting_id: firstRow.meeting_id,
        meetdate: firstRow.meetdate,
        state: firstRow.state,
        track: firstRow.track,
        track_id: firstRow.track_id,
        is_trial: firstRow.is_trial,
        results_is_complete: firstRow.results_is_complete,
        races_is_complete: firstRow.races_is_complete as CurrentMeeting['races_is_complete'],
        fields_is_complete: firstRow.fields_is_complete as CurrentMeeting['fields_is_complete'],
        interim_rating: firstRow.interim_rating as CurrentMeeting['interim_rating'],
        final_rating: firstRow.final_rating as CurrentMeeting['final_rating'],
        sectional_times: firstRow.sectional_times as CurrentMeeting['sectional_times'],
        verified_ratings: firstRow.verified_ratings as CurrentMeeting['verified_ratings'],
        d_ts: firstRow.d_ts,
      });

      setAusRatings(rows);
      setIsDirty(false);
      return rows;
    } catch (err) {
      console.error('Error in loadMeeting:', err);
      toast.error(err instanceof Error ? err.message : 'Unknown error', {
        title: 'Failed to load meeting',
      });
      throw err;
    }
  }, []);

  /** Generic update for any field — accepts optional explicit scope.
   *  Uses refs for data access so this callback stays stable across renders. */
  const updateField = useCallback(async (
    field: string,
    value: unknown,
    id: number | null = null,
    explicitScope: FormScope | null = null,
  ): Promise<MeetingRow[] | null> => {
    if (!formActiveRef.current) {
      // Form is in fast read-only mode — silently ignore writes.
      return null;
    }
    const scope = explicitScope || SCOPES.find((s) => scopeConfig[s].includes(field));
    if (!scope) throw new Error(`Unknown field: ${field}`);

    const meeting = currentMeetingRef.current;
    const ratings = ausRatingsRef.current;

    let targetId = id;
    if (!targetId) {
      if (scope === 'meeting') targetId = Number(meeting?.meeting_id);
      else if (scope === 'race') targetId = ratings.find((r) => r.race_id != null)?.race_id ?? null;
      else if (scope === 'runner') targetId = ratings.find((r) => r.runner_id != null)?.runner_id ?? null;
    }

    if (!targetId) throw new Error(`Cannot determine id for field: ${field}`);

    const payload = {
      meeting_id: meeting?.meeting_id,
      scope,
      id: targetId,
      updates: { [field]: value },
      data: ratings,
    };
    const result = await api.post<MeetingRow[]>('/api/form/update', payload);
    setAusRatings(result);

    // Sync currentMeeting for meeting-scope updates
    if (scope === 'meeting') {
      setCurrentMeeting((prev) => (prev ? { ...prev, [field]: value } : prev));
    }
    setIsDirty(true);
    return result;
  }, []); // stable — reads from refs

  /** Batch update one-or-more fields across one-or-more runners in a single
   *  flush. Each /form/update call returns the full recomputed dataset, so we
   *  CHAIN them — feeding the previous response in as `data` for the next call.
   *  Without chaining, every call would post the same pre-flush snapshot and the
   *  last write would clobber the others (e.g. editing Jx then Bx loses the Jx
   *  edit). */
  const updateRunnerFields = useCallback(async (
    pending: PendingRunnerFields,
  ): Promise<MeetingRow[] | null> => {
    if (!formActiveRef.current) return null; // fast read-only mode — ignore writes
    if (!pending || !Object.keys(pending).length) return null;

    const meeting = currentMeetingRef.current;
    let working = ausRatingsRef.current;

    for (const idStr of Object.keys(pending)) {
      const id = Number(idStr);
      const fields = pending[idStr];

      // Group this runner's fields by scope (FieldTable only emits runner-scope
      // fields today, but stay correct if that ever changes).
      const byScope: Partial<Record<FormScope, Record<string, unknown>>> = {};
      for (const field of Object.keys(fields)) {
        const scope = SCOPES.find((s) => scopeConfig[s].includes(field));
        if (!scope) throw new Error(`Unknown field: ${field}`);
        if (!byScope[scope]) byScope[scope] = {};
        byScope[scope]![field] = fields[field];
      }

      for (const scope of Object.keys(byScope) as FormScope[]) {
        const payload = {
          meeting_id: meeting?.meeting_id,
          scope,
          id,
          updates: byScope[scope],
          data: working,
        };
        working = await api.post<MeetingRow[]>('/api/form/update', payload);
      }
    }

    setAusRatings(working);
    setIsDirty(true);
    return working;
  }, []); // stable — reads from refs

  /** Stage an inspected value for DB persistence (aus_predictions).
   *  `col` is the DB column name (forecast_rating / Jx / Bx / Dx / notes).
   *  Accumulates per runner until persistForm() flushes it on Save. */
  const stagePersist = useCallback((runnerId: RunnerId | string, col: string, value: unknown) => {
    const id = Number(runnerId);
    if (!Number.isFinite(id)) return;
    persistRef.current[id] = { ...(persistRef.current[id] || {}), [col]: value };
    setIsDirty(true);
  }, []);

  /** Flush staged edits to aus_predictions via /api/form/persist. Sends one
   *  runner per entry with only the edited columns, so untouched forecasts
   *  stay NULL (and expected_rating keeps falling back to predicted_rating).
   *  Clears the staged map on success. */
  const persistForm = useCallback(async () => {
    const staged = persistRef.current;
    const runnerIds = Object.keys(staged);
    if (!runnerIds.length) return null;

    const meeting = currentMeetingRef.current;
    const runners = runnerIds.map((id) => ({ runner_id: Number(id), ...staged[Number(id)] }));

    const result = await api.post<unknown>('/api/form/persist', {
      meeting_id: meeting?.meeting_id,
      runners,
    });
    persistRef.current = {};
    return result;
  }, []);

  /** Reset dirty flag (e.g. after explicit save) */
  const clearDirty = useCallback(() => setIsDirty(false), []);

  const contextValue = useMemo<DataContextValue>(() => ({
    ausRatings,
    currentMeeting,
    loadMeeting,
    updateField,
    updateRunnerFields,
    stagePersist,
    persistForm,
    isDirty,
    clearDirty,
    formActive,
    setFormActive,
  }), [ausRatings, currentMeeting, loadMeeting, updateField, updateRunnerFields, stagePersist, persistForm, isDirty, clearDirty, formActive]);

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
};

export const useData = (): DataContextValue => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
};

export default DataContext;
